import * as Yup from "yup";

/**
 * Mirrors SetupFeeValidation, which checks its relationship with total cost
 * from BOTH sides — once here and once in TotalCostValidation — so whichever
 * field the lender is editing is the one that gets highlighted.
 *
 * The total-cost half no-ops for bid types that carry no total cost or term
 * (leasing, revolving credit, purchase financing); those pass
 * `boundByApprovedAmount` instead, which is the only bound available to them.
 */
export const AdministrationFeesValidation = (
  formatWithCurrency: (value: number) => string,
  { boundByApprovedAmount = false }: { boundByApprovedAmount?: boolean } = {},
) =>
  Yup.number()
    .optional()
    .nullable()
    .min(0, "Should be at least 0")
    .when("amount", ([amount], schema) => {
      // Only for bid types with no total cost. Where a total cost exists, the
      // total-cost rule governs and this bound can contradict it -- it would
      // reject fees the all-fees-included total demonstrably covers (e.g.
      // amount 100_000, fees 100_000, 1-month term, total cost 200_000).
      if (!boundByApprovedAmount || !amount) {
        return schema;
      }

      return schema.lessThan(
        amount,
        `Administration fees can't be equal to or larger than approved amount (${formatWithCurrency(amount)})`,
      );
    })
    .test(
      "administrationFees-totalCost-check",
      "Administration fees are not covered by the total cost.",
      function (value) {
        const { amount, setupFee, totalCost, paybackTimeMonths } = this.parent;

        if (!value || !totalCost || amount == null || !paybackTimeMonths) {
          return true; // Nothing to check against
        }

        const minimum =
          (amount as number) +
          ((setupFee as number) || 0) +
          value * (paybackTimeMonths as number);

        if ((totalCost as number) >= minimum) {
          return true;
        }

        return this.createError({
          message: `These fees are not covered by the total cost — it would need to be at least ${formatWithCurrency(minimum)} (approved amount + setup fee + ${paybackTimeMonths} months of administration fees).`,
        });
      },
    );
